import { createApp } from 'vue'
import type { ContactMeOpenDetail, ContactMeWidgetOptions } from './types'
import { BostContactMeWidgetElement } from './widget'
import DevTools from './components/DevTools.vue'
import { createWidgetI18n } from './i18n'

declare global {
  interface Window {
    BostContactMe?: {
      open: (detail?: ContactMeOpenDetail) => void
      close: () => void
      configure: (options: ContactMeWidgetOptions) => void
      element: BostContactMeWidgetElement
    }
  }
}

import { captureScriptBase } from './logoUrl'

const TAG = 'bost-contactme-widget'

captureScriptBase()

export { getWidgetLogoUrl } from './logoUrl'

function ensureElement(): BostContactMeWidgetElement {
  const existing = document.querySelector(TAG)
  if (existing instanceof BostContactMeWidgetElement) return existing

  const el = document.createElement(TAG) as BostContactMeWidgetElement
  document.body.appendChild(el)
  return el
}

export function registerContactMeWidget() {
  if (!customElements.get(TAG)) {
    customElements.define(TAG, BostContactMeWidgetElement)
  }
}

export function initContactMeWidget() {
  if (window.BostContactMe?.element) {
    return window.BostContactMe.element
  }

  registerContactMeWidget()
  const el = ensureElement()

  window.BostContactMe = {
    element: el,
    open: detail => el.open(detail),
    close: () => el.close(),
    configure: options => el.configure(options),
  }

  // Optional config via events
  const flag = '__bostContactMe_configListenerAttached__'
  if (!(window as any)[flag]) {
    ;(window as any)[flag] = true
    window.addEventListener('contactme:configure', (e: Event) => {
      const detail = (e as CustomEvent<ContactMeWidgetOptions>).detail
      if (detail) el.configure(detail)
    })
  }

  return el
}

// Global dev tools (only in dev mode)
function initDevTools() {
  if (import.meta.env.DEV && typeof window !== 'undefined' && typeof document !== 'undefined') {
    const devContainer = document.createElement('div')
    devContainer.id = 'bost-dev-tools'
    document.body.appendChild(devContainer)

    const app = createApp(DevTools)
    app.use(createWidgetI18n())
    app.mount(devContainer)

    // Store reference for cleanup if needed
    ;(window as any).__bostDevToolsApp = app
  }
}

// Auto-init on script load (WP-friendly)
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const init = () => {
    initContactMeWidget()
    initDevTools()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true })
  } else {
    init()
  }
}
